import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  CarouselInfoCardData,
  CarouselInfoCardEvents,
  CarouselInfoCardPointerPosition,
  CarouselInfoCardStrings,
} from '../types';
import { ButtonColor, ButtonFill, ButtonType, FontSize } from '../../gzIconButton/types';
import { FontSizes } from '../../../types/common/types';
import { ButtonHeights, ButtonThemes, ButtonTypes } from '../../gzButton/types';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * @summary This component is used to display a carousel of cards with information.
 * @tag gz-carousel-info-card
 * @fires info-card-close - Fired when the close button is clicked
 * @fires info-card-previous - Fired when the previous button is clicked
 * @fires info-card-next - Fired when the next button is clicked
 * @fires info-card-primary-action - Fired when the primary action button is clicked
 * @fires info-card-secondary-action - Fired when the secondary action button is clicked
 * @fires info-card-tertiary-action - Fired when the tertiary action button is clicked
 */
@customElement('gz-carousel-info-card')
export class GzCarouselInfoCardWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  cardsData: CarouselInfoCardData[] = [];

  @property({ type: Number, attribute: true, reflect: true })
  currentCardIndex = 0;

  @property({ type: String, attribute: true, reflect: true })
  closeBtnTooltipText = '';

  /**
   * The strings used in this component for default buttons. They need to be translated using translateFn!
   * The exported function <code>getCarouselInfoCardDefaultStrings()</code> can be used to get the default strings
   */
  @property({ type: Object, attribute: true, reflect: true })
  strings: CarouselInfoCardStrings;

  static get styles() {
    return styles;
  }

  get currentCardData(): CarouselInfoCardData | undefined {
    if (this.cardsData?.length < this.currentCardIndex) {
      return;
    }
    return this.cardsData[this.currentCardIndex];
  }

  render(): TemplateResult {
    return html`
      <div class="info-card-container">
        ${this.renderCloseButton()} ${this.renderHeaderTitle()} ${this.renderImage()} ${this.renderBodyTitle()}
        ${this.renderDescription()} ${this.renderButtons()} ${this.renderFooter()} ${this.renderPointer()}
      </div>
    `;
  }

  private renderCloseButton(): TemplateResult {
    return html`
      <div class="close-button-container">
        <gz-tooltip tooltipText="${this.closeBtnTooltipText}">
          <gz-icon-button
            @click="${() => this.handleAction(CarouselInfoCardEvents.CLOSE)}"
            icon="ds-icon-clear"
            class="close-button"
            fontSize="${FontSize.ICON_SIZE_24}"
            buttonFill="${ButtonFill.SECONDARY}"
            buttonColor="${ButtonColor.WHITE}"
            buttonType="${ButtonType.SQUARE}"
          >
          </gz-icon-button>
        </gz-tooltip>
      </div>
    `;
  }

  private renderHeaderTitle(): TemplateResult | undefined {
    if (!this.currentCardData?.headerTitle) {
      return;
    }
    return html`
      <div class="header-title-container">
        <gz-text fontSize="${FontSizes.BODY}" class="header-title"> ${this.currentCardData.headerTitle} </gz-text>
        <div class="divider"></div>
      </div>
    `;
  }

  private renderImage(): TemplateResult | undefined {
    if (!this.currentCardData?.imageSrc) {
      return;
    }
    return html`<div class="image-container">
      <img class="image" src="${this.currentCardData.imageSrc}" alt="" />
    </div> `;
  }

  private renderBodyTitle(): TemplateResult | undefined {
    if (!this.currentCardData?.bodyTitle) {
      return;
    }
    return html`
      <div class="body-title-container">
        <gz-text fontSize="${FontSizes.HEADING_4}" class="body-title"> ${this.currentCardData.bodyTitle} </gz-text>
      </div>
    `;
  }

  private renderDescription(): TemplateResult | undefined {
    if (!this.currentCardData?.description) {
      return;
    }

    if (Array.isArray(this.currentCardData.description)) {
      return html`
        <div class="description-container">
          ${this.currentCardData.description.map(
            description => html`
              <gz-text fontSize="${FontSizes.HEADING_6}" class="description"> ${description} </gz-text>
            `
          )}
        </div>
      `;
    }

    return html`
      <div class="description-container">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="description"> ${this.currentCardData.description} </gz-text>
      </div>
    `;
  }

  private renderButtons(): TemplateResult | undefined {
    const data = this.currentCardData;
    if (!(data?.primaryButtonText || data?.secondaryButtonText || data?.tertiaryButtonText)) {
      return;
    }
    return html`
      <div class="buttons-container">
        ${this.renderActionButton(CarouselInfoCardEvents.PRIMARY_ACTION, data.primaryButtonText)}
        ${this.renderActionButton(CarouselInfoCardEvents.SECONDARY_ACTION, data.secondaryButtonText)}
        ${this.renderActionButton(CarouselInfoCardEvents.TERTIARY_ACTION, data.tertiaryButtonText)}
      </div>
    `;
  }

  private renderActionButton(event: CarouselInfoCardEvents, text: string | undefined): TemplateResult | undefined {
    if (!text) {
      return;
    }
    return html`
      <gz-button
        id="button_${event}"
        @click="${() => this.handleAction(event)}"
        buttonHeight="${ButtonHeights.SMALL}"
        theme="${ButtonThemes.DARK}"
        color="${event === CarouselInfoCardEvents.PRIMARY_ACTION ? ButtonColor.PRIMARY : ButtonColor.WHITE}"
        type="${event === CarouselInfoCardEvents.PRIMARY_ACTION ? ButtonTypes.FULL : ButtonTypes.OUTLINE}"
      >
        ${text}
      </gz-button>
    `;
  }

  private renderFooter(): TemplateResult | undefined {
    if (this.cardsData.length <= 1) {
      return;
    }
    return html`
      <div class="footer-container">
        <div class="divider"></div>
        <div class="navigation-container">
          <div class="d-flex">
            <gz-icon-button
              @click="${this.handlePrevious}"
              icon="ds-icon-calendar_next"
              class="previous-button"
              fontSize="${FontSize.ICON_SIZE_20}"
              buttonFill="${ButtonFill.SECONDARY}"
              buttonColor="${ButtonColor.WHITE}"
              buttonType="${ButtonType.SQUARE}"
              ?disabled="${this.currentCardIndex === 0}"
            ></gz-icon-button>
            ${this.renderBullets()}
          </div>
          <gz-button
            @click="${this.handleNext}"
            class="next-button"
            theme="${ButtonThemes.DARK}"
            type="${ButtonTypes.QUIET}"
            ?disabled="${this.currentCardIndex === this.cardsData.length - 1}"
          >
            <span class="button-text">${this.strings.nextButtonText}</span>
            <gz-icon name="ds-icon-calendar_next" size="20" class="button-icon" color="${ButtonColor.WHITE}"></gz-icon>
          </gz-button>
        </div>
        <div class="footer-title-container">
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="footer-title"> ${this.getNextTitle()} </gz-text>
        </div>
      </div>
    `;
  }

  private renderBullets(): TemplateResult {
    return html` <div class="bullets-container">${this.cardsData.map((_, index) => this.renderBullet(index))}</div> `;
  }

  private renderBullet(index: number): TemplateResult {
    const classes = {
      bullet: true,
      current: index === this.currentCardIndex,
    };
    return html`<div class="${classMap(classes)}"></div>`;
  }

  private renderPointer(): TemplateResult | undefined {
    if (!this.currentCardData) {
      return;
    }
    let top, bottom, left, right, rotate;
    const height = 12; // the pointer triangle height (in px)
    const width = 18; // the pointer triangle width (in px)
    const offset = 16; // the offset of the triangle from the card corner (in px)
    const diff = (width - height) / 2;
    switch (this.currentCardData.pointerPosition) {
      case CarouselInfoCardPointerPosition.TOP_BORDER_LEFT_SIDE:
        [top, bottom, left, right, rotate] = [`-${height}px`, 'unset', `${offset}px`, 'unset', '180deg'];
        break;
      case CarouselInfoCardPointerPosition.TOP_BORDER_RIGHT_SIDE:
        [top, bottom, left, right, rotate] = [`-${height}px`, 'unset', 'unset', `${offset}px`, '180deg'];
        break;
      case CarouselInfoCardPointerPosition.BOTTOM_BORDER_LEFT_SIDE:
        [top, bottom, left, right, rotate] = ['unset', `-${height}px`, `${offset}px`, 'unset', ''];
        break;
      case CarouselInfoCardPointerPosition.BOTTOM_BORDER_RIGHT_SIDE:
        [top, bottom, left, right, rotate] = ['unset', `-${height}px`, 'unset', `${offset}px`, ''];
        break;
      case CarouselInfoCardPointerPosition.LEFT_BORDER_UPPER_SIDE:
        [top, bottom, left, right, rotate] = [`${offset + diff}px`, 'unset', `-${height + diff}px`, 'unset', '90deg'];
        break;
      case CarouselInfoCardPointerPosition.LEFT_BORDER_LOWER_SIDE:
        [top, bottom, left, right, rotate] = ['unset', `${offset + diff}px`, `-${height + diff}px`, 'unset', '90deg'];
        break;
      case CarouselInfoCardPointerPosition.RIGHT_BORDER_UPPER_SIDE:
        [top, bottom, left, right, rotate] = [`${offset + diff}px`, 'unset', 'unset', `-${height + diff}px`, '270deg'];
        break;
      case CarouselInfoCardPointerPosition.RIGHT_BORDER_LOWER_SIDE:
        [top, bottom, left, right, rotate] = ['unset', `${offset + diff}px`, 'unset', `-${height + diff}px`, '270deg'];
        break;
    }
    const pointerStyles = { top, bottom, left, right, rotate };
    return html` <div class="card-pointer" style="${styleMap(pointerStyles)}"></div> `;
  }

  private handleAction(event: CarouselInfoCardEvents): void {
    const cardData = this.cardsData[this.currentCardIndex];
    const detail = { data: cardData, index: this.currentCardIndex };

    this.dispatchEvent(new CustomEvent(event, { detail, composed: true, bubbles: true }));
    switch (event) {
      case CarouselInfoCardEvents.PRIMARY_ACTION:
        cardData.primaryButtonAction?.();
        break;
      case CarouselInfoCardEvents.SECONDARY_ACTION:
        cardData.secondaryButtonAction?.();
        break;
      case CarouselInfoCardEvents.TERTIARY_ACTION:
        cardData.tertiaryButtonAction?.();
        break;
    }
  }

  private handlePrevious(): void {
    if (this.currentCardIndex > 0) {
      this.currentCardIndex -= 1;
    }
    this.handleAction(CarouselInfoCardEvents.PREVIOUS);
  }

  private handleNext(): void {
    if (this.currentCardIndex < this.cardsData.length - 1) {
      this.currentCardIndex += 1;
    }
    this.handleAction(CarouselInfoCardEvents.NEXT);
  }

  private getNextTitle(): string {
    if (this.currentCardIndex === this.cardsData.length - 1) {
      return '';
    }
    const nextCard = this.cardsData[this.currentCardIndex + 1];
    const nextTitle = nextCard.nextTitle ?? nextCard.headerTitle ?? nextCard.bodyTitle ?? '';
    return nextTitle;
  }
}
